import React, { useState, useEffect } from 'react';
import { HiOutlineLogout } from "react-icons/hi";
import { useNavigate } from 'react-router-dom';
import ChangePassword from '../../components/ChangePassword';
import { useAuth } from '../../context/AuthContext';
import { Mail, MapPin, Phone, AlertCircle, Calendar, RefreshCw, Lock } from 'lucide-react';
import axios from 'axios';
import RestApi from '../../Apis/RestApi';
import { toast } from 'react-toastify';

function Settings() {
    const username = localStorage.getItem("username");
    const email = localStorage.getItem("email");
    const userId = localStorage.getItem("_id");
    const navigate = useNavigate();
    const [changePassClicked, setChangePassClicked] = useState(false);
    const { logout } = useAuth();
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSubscriptionData();
    }, []);

    const fetchSubscriptionData = async () => {
        try {
            setLoading(true);
            const response = await RestApi.getUserSubscriptions(userId);
            if (response.data.status) {
                setSubscriptionData(response.data.data);
            } else {
                setError("Failed to load subscription data");
            }
        } catch (err) {
            console.error("Error fetching subscription data:", err);
            setError("Failed to load subscription data");
        } finally {
            setLoading(false);
        }
    };

    const handleChangeBillingCycle = async (newCycle) => {
        // Prevent changing from annual to monthly (can only upgrade, not downgrade)
        if (subscriptionData?.subscription?.billing_cycle === 'annually' && newCycle === 'monthly') {
            toast.info("You cannot change from annual to monthly billing during an active subscription period.");
            return;
        }
        
        try {
            setLoading(true);
            // Call API to update billing cycle
            const response = await RestApi.updateBillingCycle({
                user_id: userId,
                billing_cycle: newCycle
            });
            
            if (response.data.status) {
                toast.success(`Billing cycle updated to ${newCycle}`);
                fetchSubscriptionData(); // Refresh data
            } else {
                toast.error("Failed to update billing cycle");
            }
        } catch (err) {
            console.error("Error updating billing cycle:", err);
            toast.error("Failed to update billing cycle");
        } finally {
            setLoading(false);
        }
    };

    const logoutHandler = () => {
        localStorage.removeItem("email");
        localStorage.removeItem("_id");
        localStorage.removeItem("type");
        logout();
        navigate("/login");
    };

    const ChangePasswordHandler = () => {
        setChangePassClicked(true);
    };

    // Helper function to format plan name
    const formatPlanName = (planType) => {
        if (!planType) return "Free";
        
        return planType.charAt(0).toUpperCase() + planType.slice(1).toLowerCase();
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Helper to calculate days remaining until expiry
    const calculateDaysRemaining = (endDate) => {
        if (!endDate) return null;
        
        const today = new Date();
        const expiryDate = new Date(endDate);
        const diffTime = Math.abs(expiryDate - today);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays;
    };

    // Helper to calculate progress percentage safely
    const calculateProgress = (count, max) => {
        if (max === -1) return 0; // Unlimited
        if (max === 0) return 100; // To avoid division by zero
        return Math.min(Math.round((count / max) * 100), 100);
    };

    // Helper to get billing cycle text
    const getBillingCycleText = (billingCycle) => {
        if (!billingCycle) return "Annual billing";
        return billingCycle === "monthly" ? "Monthly billing" : "Annual billing";
    };

    // Helper to check if user has an annual subscription
    const hasAnnualSubscription = () => {
        return subscriptionData?.subscription?.billing_cycle === 'annually';
    };

    return (
        <div className="flex h-screen bg-[#0f172a] text-white overflow-hidden">
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Fixed Header */}
                <div className="flex-shrink-0 p-4 sm:p-6 border-b border-gray-700">
                    <div className="flex justify-between items-center">
                        <h1 className="text-2xl sm:text-3xl font-semibold">My Account</h1>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {!changePassClicked ? (
                        <>
                            {/* My Details */}
                            <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-6">
                                <h2 className="text-xl font-semibold mb-4 text-black">My Details</h2>
                                <div className="grid gap-3">
                                    <div className="flex">
                                        <h3 className="text-black font-medium">Name:</h3>
                                        <p className="text-black ml-2">{username ? username.toUpperCase() : "N/A"}</p>
                                    </div>
                                    <div className="flex">
                                        <h3 className="text-black font-medium">Email:</h3>
                                        <p className="text-black ml-2">{email ? email.toUpperCase() : "N/A"}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2 mb-4">
                                <button className="bg-[#126f97] text-white py-2 px-4 rounded-lg text-sm sm:text-base"
                                    onClick={ChangePasswordHandler}>
                                    Change Password
                                </button>
                            </div>
                            
                            {/* Billing */}
                            <h2 className="text-xl sm:text-2xl font-semibold mb-4">Billing</h2>
                            <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-6">
                                <h3 className="font-semibold text-lg mb-4 text-black">Plan details</h3>
                                {loading ? (
                                    <p className="text-gray-500">Loading subscription details...</p>
                                ) : error ? (
                                    <div className="flex items-center text-red-500">
                                        <AlertCircle className="w-5 h-5 mr-2" />
                                        <p>{error}</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-gray-500">Current plan</th>
                                                    <th className="px-4 py-2 text-gray-500">Billing cycle</th>
                                                    <th className="px-4 py-2 text-gray-500">Status</th>
                                                    <th className="px-4 py-2 text-gray-500">Start date</th>
                                                    <th className="px-4 py-2 text-gray-500">Renewal date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="bg-white border-t">
                                                    <td className="px-4 py-2 text-gray-700">
                                                        {subscriptionData ? formatPlanName(subscriptionData.subscription.plan) : "Free"}
                                                    </td>
                                                    <td className="px-4 py-2 text-gray-700">
                                                        {subscriptionData?.subscription?.billing_cycle ? 
                                                            getBillingCycleText(subscriptionData.subscription.billing_cycle) : 
                                                            "Annual billing"}
                                                    </td>
                                                    <td className="px-4 py-2 text-gray-700">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                            subscriptionData?.subscription?.status === "ACTIVE" 
                                                                ? "bg-green-100 text-green-800" 
                                                                : subscriptionData?.subscription?.status === "EXPIRED"
                                                                ? "bg-red-100 text-red-800"
                                                                : "bg-yellow-100 text-yellow-800"
                                                        }`}>
                                                            {subscriptionData?.subscription?.status || "Active"}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2 text-gray-700">
                                                        {formatDate(subscriptionData?.subscription?.start_date)}
                                                    </td>
                                                    <td className="px-4 py-2 text-gray-700">
                                                        {subscriptionData?.subscription?.end_date ? (
                                                            <div className="flex items-center">
                                                                <span>{formatDate(subscriptionData.subscription.end_date)}</span>
                                                                {subscriptionData.subscription.status === "ACTIVE" && (
                                                                    <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                                        {calculateDaysRemaining(subscriptionData.subscription.end_date)} days left
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            "Never (Free plan)"
                                                        )}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        
                                        {subscriptionData?.subscription?.plan !== "FREE" && (
                                            <div className="mt-4 flex gap-4">
                                                <a onClick={()=>navigate("/premium")} className="text-sm text-blue-500 cursor-pointer flex items-center">
                                                    <RefreshCw className="w-4 h-4 mr-1" />
                                                    Renew subscription
                                                </a>
                                                
                                                {subscriptionData?.subscription?.billing_cycle && (
                                                    <div className="flex items-center">
                                                        <span className="text-sm text-gray-500 mr-2">Change billing cycle:</span>
                                                        <button 
                                                            onClick={() => handleChangeBillingCycle('monthly')}
                                                            className={`text-sm px-3 py-1 rounded-l-lg ${
                                                                subscriptionData.subscription.billing_cycle === 'monthly' 
                                                                    ? 'bg-purple-600 text-white' 
                                                                    : hasAnnualSubscription() 
                                                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                                        : 'bg-gray-200 text-gray-700'
                                                            }`}
                                                            disabled={hasAnnualSubscription()}
                                                        >
                                                            {hasAnnualSubscription() && (
                                                                <Lock className="inline w-3 h-3 mr-1" />
                                                            )}
                                                            Monthly
                                                        </button>
                                                        <button 
                                                            onClick={() => handleChangeBillingCycle('annually')}
                                                            className={`text-sm px-3 py-1 rounded-r-lg ${
                                                                subscriptionData.subscription.billing_cycle !== 'monthly' 
                                                                    ? 'bg-purple-600 text-white' 
                                                                    : 'bg-gray-200 text-gray-700'
                                                            }`}
                                                        >
                                                            Annually
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Billing cycle restriction message */}
                                        {hasAnnualSubscription() && (
                                            <div className="mt-2 text-xs text-gray-500 flex items-center">
                                                <AlertCircle className="w-3 h-3 mr-1" />
                                                Annual subscriptions cannot be changed to monthly until renewal
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Usage Section */}
                            <h2 className="text-xl sm:text-2xl font-semibold mb-4">Usage</h2>
                            
                            {loading ? (
                                <p className="text-white">Loading usage data...</p>
                            ) : error ? (
                                <div className="flex items-center text-red-500 bg-white p-4 rounded-lg">
                                    <AlertCircle className="w-5 h-5 mr-2" />
                                    <p>{error}</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    {/* QR Codes */}
                                    <div className="bg-white shadow rounded-lg p-4 sm:p-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-medium text-gray-700">QR Codes</span>
                                            <span className="text-sm text-gray-500">
                                                {subscriptionData?.usage?.qrCodes?.count || 0}/
                                                {subscriptionData?.usage?.qrCodes?.maxAllowed === -1 ? 
                                                    "Unlimited" : 
                                                    subscriptionData?.usage?.qrCodes?.maxAllowed || 0}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div 
                                                className="bg-blue-500 h-2.5 rounded-full" 
                                                style={{ 
                                                    width: `${subscriptionData?.usage?.qrCodes?.maxAllowed === -1 ? 
                                                        '5%' : // Show minimal bar for unlimited
                                                        calculateProgress(
                                                            subscriptionData?.usage?.qrCodes?.count || 0,
                                                            subscriptionData?.usage?.qrCodes?.maxAllowed || 0
                                                        )}%` 
                                                }}
                                            ></div>
                                        </div>
                                        
                                        <div className="mt-2 flex justify-between text-xs text-gray-500">
                                            <span>Used: {subscriptionData?.usage?.qrCodes?.count || 0}</span>
                                            <span>
                                                {subscriptionData?.usage?.qrCodes?.maxAllowed === -1 ? 
                                                    "Unlimited" : 
                                                    `Remaining: ${subscriptionData?.usage?.qrCodes?.remaining || 0}`}
                                            </span>
                                        </div>
                                        
                                        {subscriptionData?.subscription?.plan === "FREE" && (
                                            <a onClick={()=>navigate("/premium")} className="text-sm text-orange-500 mt-2 inline-block cursor-pointer">
                                                Upgrade to create more QR codes
                                            </a>
                                        )}
                                    </div>

                                    {/* Smart Links */}
                                    <div className="bg-white shadow rounded-lg p-4 sm:p-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-medium text-gray-700">Smart Links</span>
                                            <span className="text-sm text-gray-500">
                                                {subscriptionData?.usage?.links?.count || 0}/
                                                {subscriptionData?.usage?.links?.maxAllowed === -1 ? 
                                                    "Unlimited" : 
                                                    subscriptionData?.usage?.links?.maxAllowed || 0}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div 
                                                className="bg-blue-500 h-2.5 rounded-full" 
                                                style={{ 
                                                    width: `${subscriptionData?.usage?.links?.maxAllowed === -1 ? 
                                                        '5%' : // Show minimal bar for unlimited
                                                        calculateProgress(
                                                            subscriptionData?.usage?.links?.count || 0,
                                                            subscriptionData?.usage?.links?.maxAllowed || 0
                                                        )}%` 
                                                }}
                                            ></div>
                                        </div>
                                        
                                        <div className="mt-2 flex justify-between text-xs text-gray-500">
                                            <span>Used: {subscriptionData?.usage?.links?.count || 0}</span>
                                            <span>
                                                {subscriptionData?.usage?.links?.maxAllowed === -1 ? 
                                                    "Unlimited" : 
                                                    `Remaining: ${subscriptionData?.usage?.links?.remaining || 0}`}
                                            </span>
                                        </div>
                                        
                                        {subscriptionData?.subscription?.plan === "FREE" && (
                                            <a onClick={()=>navigate("/premium")} className="text-sm text-orange-500 mt-2 inline-block cursor-pointer">
                                                Upgrade to create more links
                                            </a>
                                        )}
                                    </div>
                                    
                                    {/* Total Resources */}
                                    <div className="bg-white shadow rounded-lg p-4 sm:p-6 md:col-span-2">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-medium text-gray-700">Total Resources</span>
                                            <span className="text-sm text-gray-500">
                                                {subscriptionData?.usage?.total?.count || 0}/
                                                {subscriptionData?.usage?.total?.maxAllowed === -1 ? 
                                                    "Unlimited" : 
                                                    subscriptionData?.usage?.total?.maxAllowed || 0}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div 
                                                className="bg-blue-500 h-2.5 rounded-full" 
                                                style={{ 
                                                    width: `${subscriptionData?.usage?.total?.maxAllowed === -1 ? 
                                                        '5%' : // Show minimal bar for unlimited
                                                        calculateProgress(
                                                            subscriptionData?.usage?.total?.count || 0,
                                                            subscriptionData?.usage?.total?.maxAllowed || 0
                                                        )}%` 
                                                }}
                                            ></div>
                                        </div>
                                        
                                        <div className="mt-2 flex justify-between text-xs text-gray-500">
                                            <span>Used: {subscriptionData?.usage?.total?.count || 0}</span>
                                            <span>
                                                {subscriptionData?.usage?.total?.maxAllowed === -1 ? 
                                                    "Unlimited" : 
                                                    `Remaining: ${subscriptionData?.usage?.total?.remaining || 0}`}
                                            </span>
                                        </div>
                                        
                                        {subscriptionData?.subscription?.plan === "FREE" && (
                                            <a onClick={()=>navigate("/premium")} className="text-sm text-orange-500 mt-2 inline-block cursor-pointer">
                                                Upgrade your plan for more resources
                                            </a>
                                        )}
                                        
                                        {subscriptionData?.subscription?.status === "ACTIVE" && 
                                         subscriptionData?.subscription?.billing_cycle === "monthly" && (
                                            <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-2 rounded-lg flex items-center">
                                                <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                                                Monthly resources reset on {formatDate(subscriptionData?.subscription?.end_date)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Contact Us */}
                            <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-8">
                                <h2 className="font-medium text-gray-700 mb-4">Contact Us</h2>
                                <div className="space-y-3">
                                    <div className="flex items-center">
                                        <Mail className="w-5 h-5 text-orange-500 mr-2 flex-shrink-0" />
                                        <a href="mailto:support@smart-qrcodes.com" className="text-sm text-blue-600 hover:underline">
                                            support@smart-qrcodes.com
                                        </a>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Logout Button */}
                            <button className="text-red-800 bg-slate-300 py-3 px-6 flex items-center rounded-lg"
                                onClick={logoutHandler}>
                                Logout <HiOutlineLogout className="ml-2" />
                            </button>
                        </>
                    ) : (
                        <ChangePassword />
                    )}
                </div>
            </div>
        </div>
    );
}

export default Settings;